(ns frontend.view.settings2.permissions.fields
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.permissions.entity-permissions :as entity-permissions]
   [common.schema.assets :as asset-schema]
   [common.schema.entity-types :as entity-types]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.actions.tenant :as tenant-actions]
   [frontend.db :as app-db]
   [frontend.logging :as logging]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.ui.component.settings.custom-field-modal :as custom-field-modal]
   [frontend.utils :as fu]
   [frontend.view.modals.confirmation.view :as confirmation-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.settings2.permissions.events :as events]
   [frontend.view.settings2.permissions.field-settings.db :as db]
   [frontend.view.settings2.permissions.field-settings.table :as settings-table]
   [frontend.view.settings2.permissions.sub :as sub]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.breadcrumbs.view :as breadcrumbs]
   [ui.components.button.view :as button]
   [ui.components.radix-ui.accordion.view :as accordion]
   [ui.dom-globals :as dg]
   [ui.util :as uu]))

(defn save-changes!
  [{:keys [zadmin-state settings-state table-state]} on-success!]
  (let [table-state-snapshot @table-state
        zadmin-state-snapshot @zadmin-state
        settings-state-snapshot @settings-state
        ts-before-request (:common/updated-on @session/tenant)]
    (tenant-actions/*->set-field-settings
     (merge {:tenant/field-configurations (potpuri/deep-merge
                                           (:tenant/field-configurations @session/tenant)
                                           @settings-state)}
            (when (session/zadmin?)
              {:tenant/blacklisted-fields        (:tenant/blacklisted-fields @zadmin-state)
               :tenant/whitelisted-custom-fields (:tenant/whitelisted-custom-fields
                                                  @zadmin-state)}))
     (fn [_success]
       (session/wait-for-entity-update!
        (:db/id @session/tenant)
        ts-before-request
        (fn [_]
          ;; after processing done clear local settings --> ONLY if nothing has changed
          (when (and (= @table-state table-state-snapshot)
                     (= @zadmin-state zadmin-state-snapshot)
                     (= @settings-state settings-state-snapshot))
            (swap! table-state assoc :save-counter (inc (:save-counter @table-state)))
            (reset! zadmin-state (select-keys @session/tenant
                                              [:tenant/blacklisted-fields
                                               :tenant/whitelisted-custom-fields]))
            (reset! settings-state {})
            ;; if atom is cursor we must flush it
            (deref zadmin-state)
            (deref settings-state)
            (when on-success!
              (on-success!))))))
     (fn [err]
       (logging/error "Failed to save settings" err)))))

(defn toolbar-buttons
  [{:keys [zadmin-state settings-state] :as states} _toolbar-state]
  (let [no-updates? (and (empty? @settings-state)
                         (or (not (session/zadmin?))
                             (= @zadmin-state
                                (select-keys @session/tenant
                                             [:tenant/blacklisted-fields
                                              :tenant/whitelisted-custom-fields]))))]
    [{:toolbar-button?  true
      :dropdown-action? true
      :ord              1
      :primary-button?  (not no-updates?)
      :disabled?        no-updates?
      :icon             :fa-star
      :on-select        (fn [_]
                          (save-changes! states nil))
      :label            (if no-updates?
                          (loc/tr :no-updates)
                          (loc/tr :save))}]))

(defn breadcrumbs
  [entity-type]
  [breadcrumbs/container
   [breadcrumbs/item
    {:on-click (fn [_e]
                 (routing/set-view! [:account-settings :tab-key "config"]))}
    (loc/tr :settings/account-settings)]
   [breadcrumbs/item
    {:active? true}
    (loc/tr :entity-type-permissions [(loc/tr entity-type)])]])

(defn create-modal
  [settings-state table-state entity-type]
  (let [similar-fields @(rf/subscribe [::sub/similar-fields])
        new-field @(rf/subscribe [::sub/new-field])]
    [custom-field-modal/create-dialog
     {:visible?       @(rf/subscribe [::sub/create-dialog-visible?])
      :entity-type    entity-type
      :size           :xs
      :hide!          (fn [_]
                        (rf/dispatch [::events/hide-create-dialog!]))
      :status         @(rf/subscribe [::sub/create-dialog-status])
      :select!        (fn [existing-field]
                        (rf/dispatch [::events/hide-create-dialog!])
                        (swap! settings-state assoc-in
                               [entity-type existing-field :field-settings/in-use-config]
                               :in-use)
                        (swap! table-state assoc
                               :search (when existing-field
                                         (some->> (loc/field-name
                                                   existing-field
                                                   (registry/get-field-schema @session/tenant-schema
                                                                              existing-field))
                                                  (cu/str-trim-lcase)))
                               :show-inactive? true))
      :on-save!       (fn [_custom-field]
                        (rf/dispatch [::events/create-status! :status/processing])
                        (tenant-actions/*->create-custom-field
                         (assoc (select-keys new-field
                                             [:field/name
                                              :field/field-type
                                              :field/short-name])
                           :field/for-entity-types
                           [entity-type])
                         (fn [resp]
                           (let [new-field (:body resp)
                                 field-attr (:field/attribute new-field)]
                             (rf/dispatch [::events/hide-create-dialog!])
                             (swap! settings-state assoc-in
                                    [entity-type field-attr :field-settings/in-use-config]
                                    :in-use)
                             (swap! session/tenant assoc
                                    :tenant/custom-field-definitions
                                    (concat (:tenant/custom-field-definitions @session/tenant)
                                            [new-field]))
                             (swap! table-state assoc
                                    :search (when field-attr
                                              (some->> (loc/field-name
                                                        field-attr
                                                        (registry/get-field-schema @session/tenant-schema
                                                                                   field-attr))
                                                       (cu/str-trim-lcase)))
                                    :show-inactive? true)))
                         (fn [err]
                           (logging/unimplemented "Create Failed : " err)
                           (rf/dispatch [::events/create-error err]))))
      :valid-field?   (fn [custom-field]
                        (and (not (cu/blank-value? (:field/name custom-field)))
                             (:field/field-type custom-field)))
      :similar-fields similar-fields}]))


(def tab-groups
  [:tab.group/opportunity
   :tab.group/asset
   :tab.group/activity
   :tab.group/decision
   :tab.group/risk
   :tab.group/counterparty-diligence
   :tab.group/map-profiles
   :tab.group/other])

(defn resolve-entity-tab-group
  [entity-type]
  (cond
    (some #{entity-type}
          [:entity.type/oppo :entity.type/sales-opportunity
           :entity.type/exit-opportunity :entity.type/fundraising-opportunity])
    :tab.group/opportunity

    (some #{entity-type} asset-schema/all-asset-types)
    :tab.group/asset

    (some #{entity-type} [:entity.type/risk :entity.type/risk-holding])
    :tab.group/risk

    (entity-types/decision-or-associated-entity-type entity-type)
    :tab.group/decision

    (some #{entity-type} [:entity.type/note])
    :tab.group/activity

    (some #{entity-type} [:entity.type/project-counterparty-diligence
                          :entity.type/company-counterparty-diligence
                          :entity.type/person-counterparty-diligence])
    :tab.group/counterparty-diligence

    (contains? #{:entity.type/graph-company
                 :entity.type/graph-person
                 :entity.type/graph-fund
                 :entity.type/graph-fof-asset} entity-type)
    :tab.group/map-profiles

    :else :tab.group/other))

(defn side-nav
  [{:keys [entity-type tabs _on-change]}]
  (let [tabs-by-group
        (->> tabs
             (group-by (fn [{:keys [tab-key]}]
                         (resolve-entity-tab-group tab-key))))
        visible-groups
        (->> tab-groups
             (map (fn [tab-group-key]
                    (let [members (get tabs-by-group tab-group-key)]
                      {:tab/key     tab-group-key
                       :tab/label   (loc/tr tab-group-key)
                       :tab/members members})))
             (filter (fn [{:keys [tab/members]}]
                       (seq members))))
        scrolled-state (r/atom false)
        accordion-value-state (r/atom (resolve-entity-tab-group entity-type))]
    (fn [{:keys [entity-type on-change]}]
      (let [accordion-value @accordion-value-state]
        [:div
         {:class     "px-3 py-2 w-64 h-[calc(100vh_-_110px)] text-base-light-text
                      border-e border-solid border-base-light-border-paler
                      bg-base-light
                      overflow-y-auto overflow-x-hidden"
          :on-scroll (fn [e]
                       (let [scroll-top (dg/event-target-scroll-top e)]
                         (if (< 7 scroll-top)
                           (reset! scrolled-state true)
                           (reset! scrolled-state false))))}

         [:div
          {:class "mb-3 text-base-light-text text-xl font-semibold"}
          (loc/tr :entity-types)]


         [accordion/accordion-root
          {:collapsible   false
           :type          :single
           :value         (-> accordion-value
                              (str)
                              (subs 1))
           :onValueChange (fn [str-value]
                            (reset! accordion-value-state (keyword str-value)))}

          (->> visible-groups
               (map
                (fn [tab-group]
                  (let [group-key (:tab/key tab-group)
                        open? (= group-key accordion-value)
                        label (:tab/label tab-group)
                        members (:tab/members tab-group)]
                    ^{:key (str "tab-group-" group-key)}
                    [accordion/accordion-item
                     {:value (-> group-key
                                 (str)
                                 (subs 1))}
                     [accordion/accordion-header
                      [accordion/accordion-trigger
                       {:class "py-2 px-0 flex items-center gap-2 text-base-light-text hover:text-base-light-text-accent"}
                       [:span
                        label]
                       [:i
                        {:class (str "fal fa-chevron-down ease-linear "
                                     (when open?
                                       "rotate-180"))}]]]

                     [accordion/accordion-content
                      {}
                      [:ul
                       {:class ""}
                       (->> members
                            (sort-by :tab-label)
                            (map (fn [{:keys [tab-key tab-label]}]
                                   (let [active? (= tab-key entity-type)]
                                     ^{:key (str "tab-item-" tab-key)}
                                     [:li
                                      {:class    (str "px-4 py-2 text-base-light-text cursor-pointer hover:text-base-light-text-accent "
                                                      (when active?
                                                        "text-underline"))
                                       :on-click #(on-change tab-key)}
                                      [:div

                                       [:p tab-label]]])))
                            (doall))]]])))
               (doall))]
         [:div
          {:class "h-24"}]]))))

(defn custom-field-button
  [{:keys [entity-type]}]
  (when (and (session/has-feature-access? :feature/custom-fields)
             (contains? entity-permissions/custom-field-entity-types entity-type))
    [button/sm-secondary-hollow
     {:on-click (fn [e]
                  (dg/prevent-and-stop e)
                  (rf/dispatch [::events/open-create-dialog]))}
     (loc/tr :custom-fields/create)]))

(defn component
  [entity-type]
  (let [toolbar-atom (r/atom {})
        tabs
        (->>
         entity-permissions/CONFIGURABLE_ENTITIES
         (remove
          (fn [et]
            (cond
              (entity-types/decision-or-associated-entity-type et)
              (not (session/decisions?))
              :else
              (case et
                :entity.type/fof-asset (not (session/has-feature-access? :feature/asset-types-fof))
                :entity.type/other-asset (not (session/has-feature-access?
                                               :feature/asset-types-other))
                :entity.type/real-estate (not (session/has-feature-access?
                                               :feature/asset-types-real-estate))
                :entity.type/fund (not (session/has-feature-access? :feature/fund-management))
                :entity.type/holding (and (not (session/has-feature-access? :feature/fundraising))
                                          (not (session/has-feature-access?
                                                :feature/fund-management)))
                :entity.type/fundraising-opportunity (not (session/has-feature-access?
                                                           :feature/fundraising))
                :entity.type/oppo (not (session/has-feature-access? :feature/dealflow))
                :entity.type/sales-opportunity (not (session/has-feature-access?
                                                     :feature/sales-pipelines))
                :entity.type/sale-agreement (not (session/has-feature-access?
                                                  :feature/sales-pipelines))
                :entity.type/exit-opportunity (not (session/has-feature-access?
                                                    :feature/exit-process-management))
                :entity.type/project-counterparty-diligence
                (not (session/counterparty-diligence?))
                :entity.type/company-counterparty-diligence
                (not (session/counterparty-diligence?))
                :entity.type/person-counterparty-diligence
                (not (session/counterparty-diligence?))
                :entity.type/transaction-process
                (not (session/transaction-process?))
                :entity.type/fund-commitment
                (and (not (session/has-feature-access? :feature/fund-management))
                     (not (session/has-feature-access? :feature/fundraising)))
                :entity.type/investor
                (and (not (session/has-feature-access? :feature/fundraising))
                     (not (session/has-feature-access? :feature/fund-management))
                     (not (session/has-feature-access? :feature/kyc))
                     (not (session/has-feature-access? :feature/sales-pipelines))
                     (not (session/has-feature-access? :feature/exit-process-management)))
                :entity.type/value-creation (not (session/has-feature-access?
                                                  :feature/value-creation-activity))
                :entity.type/graph-company (not (session/entity-graph?))
                :entity.type/graph-person (not (session/entity-graph?))
                :entity.type/graph-fund (not (session/entity-graph?))
                :entity.type/graph-fof-asset (not (session/entity-graph?))
                :entity.type/risk (not (session/has-feature-access? :feature/risk-module))
                :entity.type/risk-holding (not (session/has-feature-access? :feature/risk-module))

                false))))
         (map (fn [et]
                {:tab-key   et
                 :tab-label (loc/tr et)}))
         (into []))
        available-entity-types (->> tabs
                                    (map :tab-key)
                                    (into []))
        entity-type (->> entity-permissions/CONFIGURABLE_ENTITIES
                         (filter (fn [et]
                                   (= et entity-type)))
                         (remove nil?)
                         (first))
        first-entity-type (get available-entity-types entity-type (first available-entity-types))
        table-state (r/cursor app-db/app-state [:field-config])
        _ (swap! table-state assoc
                 :entity-type first-entity-type
                 :show-inactive? false
                 :search nil
                 :save-counter 0)
        _ (deref table-state)
        initial-zadmin-state
        (fn []
          (select-keys @session/tenant
                       [:tenant/blacklisted-fields :tenant/whitelisted-custom-fields]))
        zadmin-state (r/atom (initial-zadmin-state))
        settings-state (r/atom {})
        _ (swap! db/view-settings assoc :sort-by :field-settings/field-name :sort-direction "ASC")
        args {:zadmin-state   zadmin-state
              :settings-state settings-state
              :table-state    table-state}
        unload-handler
        (fn [new-view new-view-params]
          (when-not (and (empty? @settings-state)
                         (or (not (session/zadmin?))
                             (= @zadmin-state
                                (select-keys @session/tenant
                                             [:tenant/blacklisted-fields
                                              :tenant/whitelisted-custom-fields]))))
            (modals/open-modal!
             confirmation-modal/modal-key
             {:body           [:p (loc/tr :you-have-unsaved-settings-confirm-body)]
              :title          (loc/tr :unsaved-changes)
              :cancel-text    (loc/tr :discard-changes-and-exit)
              :confirm-text   (loc/tr :save-settings-and-exit)
              :confirm-color  :c/primary
              :skip-checkbox? true
              :on-confirm     (fn [_]
                                (save-changes! args #(routing/set-view! new-view new-view-params)))
              :on-cancel      (fn [_]
                                (reset! settings-state {})
                                (when (session/zadmin?)
                                  (reset! zadmin-state (initial-zadmin-state)))
                                (routing/set-view! new-view new-view-params))})
            ;; prevent unloading of this page
            true))]
    (r/create-class
     {:component-did-mount
      (fn [_]
        (routing/add-change-interceptor :field-config-interceptor unload-handler))
      :component-will-unmount
      (fn [_]
        (routing/remove-change-interceptor :field-config-interceptor))
      :reagent-render
      (fn [entity-type]
        [:div
         {:class (uu/cm "flex flex-col flex-1")}
         [zap-toolbar/view toolbar-atom (partial breadcrumbs entity-type) nil (partial toolbar-buttons args)]

         [:div
          {:class "flex flex-1"}
          [side-nav
           {:tabs        tabs
            :entity-type entity-type
            :on-change   (fn [tab-key]
                           (rf/dispatch [::events/select-entity-type tab-key])
                           (swap! table-state assoc
                                  :entity-type tab-key
                                  :show-inactive? false
                                  :search nil)
                           (swap! db/view-settings assoc
                                  :sort-by :field-settings/field-name
                                  :sort-direction "ASC")

                             ;; We MUST deref atom in case it's cursor - there is no way to know so we should do it on every swap!
                           (deref table-state))}]

          [:div
           {:class "flex flex-col flex-1 text-base-light-text bg-base-light "}
           [create-modal settings-state table-state entity-type]
           [:div
            {:class "py-2 px-2 flex items-center gap-4"}
            [:div
             {:class-name "min-w-72 relative"}
             [:input
              {:class-name  (fu/cs "py-2 px-3 w-full text-input-light-text placeholder:text-input-light-text-paler outline-0 border border-solid border-input-light-border-paler hover:border-input-light-border focus:ring-1 focus:ring-inset focus:ring-input-light-border focus-visible:ring-input-light-border rounded-full bg-base-light")
               :type        "text"
               :on-click    identity
               :value       (:search @table-state)
               :on-change   (fn [e]
                              (swap! table-state assoc :search (dg/event-target-value e)))
               :placeholder (loc/tr :search-field)}]
             [:i
              {:class-name "fal fa-search absolute top-2/4 -translate-y-2/4 right-3"}]]
            (let [{:keys [show-inactive?]} @table-state]
              [button/secondary-open
               {:on-click (fn [e]
                            (dg/prevent-and-stop e)
                            (swap! table-state update :show-inactive? not))}
               (if show-inactive?
                 (loc/tr :hide-inactive-fields)
                 (loc/tr :show-all-available-fields))])

            [custom-field-button {:entity-type entity-type}]]

           [:div
            {:id    (str "permissions-" (:entity-type @table-state))
             :class "flex flex-1 flex-col border-base-light-border-paler"}

            ^{:key (str "permissions-entity-type-" (:entity-type @table-state))}
            [settings-table/view
             {:table-state          table-state
              :zadmin-state         zadmin-state
              :settings-state       settings-state
              :local-storage-path   [(:db/id @session/tenant) :tenant/field-configurations]
              :show-zadmin-columns? true}]]]]])})))

(defn view
  [entity-type]
  (rf/dispatch-sync [::events/select-entity-type entity-type])
  (fn [_entity-type]
    (let [entity-type @(rf/subscribe [::sub/selected-entity-type])]
      [default-page-layout/view
       [component entity-type]])))

(defmethod routing/render-view :settings.permissions/fields
  [{:keys [route-params _view-params]}]
  (let [entity-type-str (:entity-type route-params)
        entity-type (if entity-type-str
                      (->> entity-permissions/CONFIGURABLE_ENTITIES
                           (filter (fn [et]
                                     (= (string/lower-case entity-type-str) (string/lower-case (loc/tr et)))))
                           (remove nil?)
                           (first))
                      :entity.type/oppo)]
    [view entity-type]))
